<template>
    <AccountAction>
        <template #title>
           Sign in with email
        </template>
        <template #default>
            <div class="flex flex-col gap-4">
                <AppInputText
                    id="sign-in-email"
                    name="email"
                    label="Email"
                    type="email"
                    v-model="username"
                    :required="true"
                    @blur="validateEmailInput"
                    @keyup="validateEmailInput"
                />

                <AppInputText
                    id="sign-in-password"
                    name="password"
                    label="Password"
                    :type="inputMode"
                    v-model="password"
                    :required="true"
                    @blur="validateUserPassword"
                    @keyup="validateUserPassword"
                >
                    <template #suffix>
                        <button
                            type="button"
                            @click="handleIconClick"
                            class="text-normal-standard hover:text-normal-emphasis"
                        >
                            <i :class="[
                                'fas',
                                inputMode === 'password' ? 'fa-eye' : 'fa-eye-slash'
                            ]"></i>
                        </button>
                    </template>
                </AppInputText>

                <!-- Explicit error message display -->
                <p v-if="hasError('password').value" class="text-sm text-critical-text mt-1">
                    {{ errors.get('password') }}
                </p>
            </div>
        </template>
        <template #actions>
            <div class="flex flex-col w-full">
                <AppButton class="w-full"
                    title="Sign In"
                    @click="handleSignIn"
                />
                <AppCancel class="mt-4" @click="$emit('cancel')" />
            </div>
        </template>
        <template #footer>
            <div class="flex justify-center mt-4">
                <AppLink
                    buttonType="Secondary"
                    title=""
                    @click="$emit('recoverPassword', username)"
                >Recover password</AppLink>
            </div>
        </template>
    </AccountAction>
</template>

<script setup lang="ts">
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { ref } from "vue";
import AccountAction from "@/components/AccountAction.vue";
import AppButton from "@/components/AppButton.vue";
import AppInputText from "@/components/AppInputText.vue";
import useFormValidation from "@/modules/useFormValidation";
import Config from "@/config/Config";

const auth = getAuth();

const {
    validateRequiredField,
    validateEmailField,
    errors,
    hasError
} = useFormValidation();

interface Props {
    username: string;
    password: string;
}

const props = defineProps<Props>();

const username = ref(props.username);
const password = ref(props.password);
const inputMode = ref("password");

const validateEmailInput = () => validateEmailField("email", "Email", username.value);

const validateUserPassword = async () => {
    validateRequiredField("password", "Password", password.value);
};

const validateAll = () => {
    validateEmailInput();
    validateUserPassword();
};

defineEmits(["createUser", "recoverPassword", "cancel"]);

const handleSignIn = async () => {
    validateAll();
    try {
        await signInWithEmailAndPassword(auth, username.value, password.value);
    } catch (error) {
        // Set error on both fields to ensure it's visible
        errors.set("password", "The email and the password you entered don't match");
        console.log(hasError("password").value);
        // Force UI update by triggering validation again

        // Add console log for debugging
        errors.forEach((value, key) => {
            console.log(`Error for ${key}: ${value}`);
        });
        console.log("Login error:", error);
        console.log("Current errors:", Array.from(errors.entries()));
    }
};

const handleIconClick = () => {
    inputMode.value = inputMode.value === "password" ? "text" : "password";
};
</script>

